import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { resetPasswordWithOtp } from '../../../services/apiServices';
import { CheckCircle2 } from 'lucide-react';
import LogoBlue from '../../../assets/images/Logo-Blue.png';
import Logo from '../../../components/common/Logo';
import { passwordValidationsMessages } from '../../../constants';
import ValidationIcon from '../../../components/common/ValidateIcon';
import { validatePassword as getPasswordValidation } from '../../../utils/passwordUtils';
import { HeaderWithLogo } from '../components/HeaderWithLogo';
import { ScreenHeader } from '../components/ScreenHeader';
import { SubmitButton } from '../components/SubmitButton';
import { BackToLoginButton } from '../components/BackToLoginButton';
import { PasswordInputWithToggle } from '../../../components/common/PasswordInputWithToggle';
import { StatusMessage } from '../../../components/common/StatusMessage';
import { getErrorMessage } from '../../../utils/errorHandler';
import { SCREENS } from '../constants';

/**
 * Change Password Screen Component
 * Final step in the password reset flow - user sets new password
 */
const ChangePasswordScreen = ({ onNavigate, email }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordValidation, setPasswordValidation] = useState(getPasswordValidation(''));
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();

  const passwordsMatch = !confirmPassword || newPassword === confirmPassword;
  const allRequirementsMet = Object.values(passwordValidation).every(Boolean);

  const validatePassword = (password) => {
    if (password.length < 8) return 'Password must be at least 8 characters long';
    if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
    if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsUpdating(true);

    try {
      await resetPasswordWithOtp({
        email,
        password: newPassword,
        verifyPassword: confirmPassword,
      });

      setShowSuccess(true);
    } catch (err) {
      setError(getErrorMessage(err) || 'Could not update password. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleBackClick = () => {
    onNavigate(SCREENS.VERIFY_CODE);
  };

  const handleGoToLogin = () => {
    navigate('/');
  };

  if (showSuccess) {
    return (
      <div className="flex flex-col items-center justify-center p-3 sm:p-4 md:p-6">
        <div className="w-full max-w-xs mx-auto">
          <Logo
            URL={LogoBlue}
            name="Janway Logo"
            variant="small"
            clickable={false}
            className="mb-4 mx-auto"
          />
        </div>

        <div className="w-full max-w-[450px]">
          <div className="content-stretch flex flex-col gap-[32px] items-center w-full">
            <div className="flex items-center justify-center">
              <CheckCircle2 className="w-[80px] h-[80px] text-green-600" />
            </div>

            <div className="content-stretch flex flex-col gap-[8px] items-center text-center leading-[normal] relative shrink-0 text-black w-full">
              <p
                className="font-['Roboto:Medium',sans-serif] font-medium relative shrink-0 text-[24px] md:text-[28px] lg:text-[32px] w-full"
                style={{ fontVariationSettings: "'wdth' 100" }}
              >
                Password Updated
              </p>
              <p
                className="font-['Roboto:Regular',sans-serif] font-normal relative shrink-0 text-[16px] md:text-[17px] lg:text-[18px] w-full"
                style={{ fontVariationSettings: "'wdth' 100" }}
              >
                Your password has been successfully updated. You can now log in with your new
                password.
              </p>
            </div>

            <button
              onClick={handleGoToLogin}
              className="h-[56px] rounded-[10px] w-full flex items-center justify-center font-['Roboto:Medium',sans-serif] font-medium text-[16px] text-center text-white border-none cursor-pointer bg-[#61b8e7] hover:bg-[#4fa5d5] transition-colors"
              style={{ fontVariationSettings: "'wdth' 100" }}
            >
              Go to Log In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="w-full max-w-[450px]">
        <HeaderWithLogo onBackClick={handleBackClick} disabled={isUpdating} />

        <div className="content-stretch flex flex-col gap-[20px] items-start relative shrink-0 w-full">
          <ScreenHeader
            icon="lock"
            title="Create new password"
            description="Choose a strong password for your account."
          />

          <form
            onSubmit={handleSubmit}
            className="content-stretch flex flex-col gap-[32px] items-center relative shrink-0 w-full"
          >
            <div className="content-stretch flex flex-col gap-[20px] items-start relative shrink-0 w-full">
              <PasswordInputWithToggle
                label="New Password"
                value={newPassword}
                onChange={(e) => {
                  const value = e.target.value;
                  setNewPassword(value);
                  setPasswordValidation(getPasswordValidation(value));
                  setError('');
                }}
                placeholder="Enter new password"
                showPassword={showNewPassword}
                onToggleVisibility={() => setShowNewPassword(!showNewPassword)}
                disabled={isUpdating}
              />

              <PasswordInputWithToggle
                label="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setError('');
                }}
                placeholder="Re-enter new password"
                showPassword={showConfirmPassword}
                onToggleVisibility={() => setShowConfirmPassword(!showConfirmPassword)}
                error={confirmPassword && !passwordsMatch ? 'Passwords do not match' : ''}
                disabled={isUpdating}
              />

              {/* Password Requirements */}
              <ul className="mt-2">
                {Object.keys(passwordValidation).map((key) => {
                  const isValid = passwordValidation[key];
                  const textColorClass = isValid ? 'text-custom-success-color' : 'text-gray-500';
                  return (
                    <li key={key} className={`flex items-center text-[15px] ${textColorClass}`}>
                      <span className="flex items-center mr-2">
                        <ValidationIcon isValid={isValid} />
                      </span>
                      {passwordValidationsMessages[key]}
                    </li>
                  );
                })}
              </ul>

              <StatusMessage type="error" message={error} />
            </div>

            <SubmitButton
              onClick={handleSubmit}
              disabled={!newPassword || !confirmPassword || !passwordsMatch || !allRequirementsMet}
              isLoading={isUpdating}
              loadingText="Updating..."
            >
              Update Password
            </SubmitButton>
          </form>

          <BackToLoginButton onClick={handleGoToLogin} disabled={isUpdating} />
        </div>
      </div>
    </>
  );
};

export default ChangePasswordScreen;
